import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Stack, Chip, Table, TableHead, TableRow, TableCell, TableBody, Pagination,
} from '@mui/material';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import { ENDPOINTS, apiCall } from '../utils/api.js';

const fmt = (s) => { if (!s) return '—'; try { return new Date(s).toLocaleDateString(); } catch { return s; } };
const money = (a, c) => `${Number(a).toFixed(2)} ${c || ''}`.trim();
export const USAGE_PAGE_SIZE = 10;

// One channel's usage line (SMS / Email): sent / included, with an "over" chip when applicable.
function UsageRow({ icon, label, sent, included, over }) {
  const cap = included ? Number(included).toLocaleString() : 'Unlimited';
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 0.5 }}>
      {icon}
      <Typography variant="body2" sx={{ minWidth: 56 }}>{label}</Typography>
      <Typography variant="body2" fontWeight={600}>
        {Number(sent || 0).toLocaleString()} <Typography component="span" variant="caption" color="text.secondary">/ {cap}</Typography>
      </Typography>
      {over > 0 && (
        <Chip size="small" label={`+${Number(over).toLocaleString()} over`}
          sx={{ bgcolor: '#fce4ec', color: '#c62828', fontWeight: 600, height: 20 }} />
      )}
    </Stack>
  );
}

// Live SMS/email usage for the CURRENT billing cycle. Past periods are in UsageHistory (paginated).
export function UsagePanel({ usage, standalone = false }) {
  if (!usage) return null;
  return (
    <Box sx={standalone ? {} : { mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>Usage this cycle</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        {fmt(usage.period_start)} – {fmt(usage.period_end)}. Overage beyond your plan is billed on your next invoice.
      </Typography>
      <UsageRow icon={<SmsOutlinedIcon fontSize="small" color="action" />} label="SMS"
        sent={usage.sms_sent} included={usage.sms_included} over={usage.sms_overage} />
      <UsageRow icon={<EmailOutlinedIcon fontSize="small" color="action" />} label="Email"
        sent={usage.email_sent} included={usage.email_included} over={usage.email_overage} />
    </Box>
  );
}

// Past-period usage snapshots, paginated (server-side). Self-fetching.
export function UsageHistory({ orgId, refreshKey = 0 }) {
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    let alive = true;
    apiCall(ENDPOINTS.ORG_USAGE_HISTORY(orgId, page))
      .then((d) => { if (!alive) return; setRows((d && (d.results || d)) || []); setCount((d && d.count) || 0); setLoaded(true); })
      .catch(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
  }, [orgId, page, refreshKey]);
  if (loaded && rows.length === 0 && page === 1) return null;
  return (
    <Box sx={{ mt: 3, overflowX: 'auto' }}>
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Past periods</Typography>
      <Table size="small">
        <TableHead><TableRow>
          <TableCell>Period</TableCell><TableCell align="right">SMS</TableCell>
          <TableCell align="right">Email</TableCell><TableCell align="right">Overage</TableCell>
        </TableRow></TableHead>
        <TableBody>
          {rows.map((h, i) => (
            <TableRow key={i}>
              <TableCell>{fmt(h.period_start)}</TableCell>
              <TableCell align="right">{h.sms_sent}{h.sms_overage ? ` (+${h.sms_overage})` : ''}</TableCell>
              <TableCell align="right">{h.email_sent}{h.email_overage ? ` (+${h.email_overage})` : ''}</TableCell>
              <TableCell align="right">{Number(h.overage_amount) > 0 ? money(h.overage_amount, h.currency) : '—'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {count > USAGE_PAGE_SIZE && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination count={Math.ceil(count / USAGE_PAGE_SIZE)} page={page}
            onChange={(_, p) => setPage(p)} size="small" color="primary" />
        </Box>
      )}
    </Box>
  );
}
